/**
 * The entry form's state and its conversion to the API document (batch 8 T12c3, #3033).
 *
 * The draft holds exactly what the user typed (text). `buildDocument` turns it into the wire document the strict schema of
 * `supplier-invoices.schema.ts` accepts, refusing here only what that schema refuses (bounds, shapes, the sign rules of the document
 * kind, gross = net + VAT of the PRINTED totals). It never computes or "fixes" a printed total, a line net or a VAT amount: whatever
 * the user typed as the document's own figures is what is sent, and a mismatch with the calculation is the server's finding.
 * Every error is a stable code the form translates.
 */

import {
  eurosToCents,
  eurosToUnitCents,
  parsePercent,
  parseSignedInteger,
  type DecimalResult,
} from './money';
import type { AllocationProposal, InvoiceDocumentBody, InvoiceDocumentKind, InvoiceLineBody } from '@/hooks/useSupplierInvoices';

export const MAX_LINES = 200;
export const MAX_LINKS_PER_LINE = 50;
export const MAX_QUANTITY = 1_000_000;

export interface LinkDraft {
  id: string;
  /** A receipt line id (allocation) or a return line id (credit link), chosen from names, never typed. */
  refId: string;
  quantity: string;
}

export interface LineDraft {
  id: string;
  /** The PO line chosen from its number, SKU and name; `''` = a line the order does not have. */
  poLineId: string;
  description: string;
  /** Signed. */
  quantity: string;
  /** Euros, up to six decimals (four decimals of a cent). */
  unitPrice: string;
  discount: string;
  vat: string;
  /** Euros as printed on the document; empty = the document did not state one. */
  statedNet: string;
  allocations: LinkDraft[];
  creditLinks: LinkDraft[];
}

export interface InvoiceDraft {
  documentKind: InvoiceDocumentKind;
  invoiceNumber: string;
  invoiceDate: string;
  shippingNet: string;
  shippingVat: string;
  statedNet: string;
  statedVat: string;
  statedGross: string;
  lines: LineDraft[];
}

let counter = 0;
export const newLocalId = (): string => `d${(counter += 1)}`;

export const emptyLine = (): LineDraft => ({
  id: newLocalId(),
  poLineId: '',
  description: '',
  quantity: '',
  unitPrice: '',
  discount: '',
  vat: '',
  statedNet: '',
  allocations: [],
  creditLinks: [],
});

export const emptyDraft = (documentKind: InvoiceDocumentKind = 'invoice'): InvoiceDraft => ({
  documentKind,
  invoiceNumber: '',
  invoiceDate: '',
  shippingNet: '',
  shippingVat: '',
  statedNet: '',
  statedVat: '',
  statedGross: '',
  lines: [emptyLine()],
});

/** A stable error code per field path (`lines.0.quantity`). The form turns it into words with `errors.<code>`. */
export type DraftErrors = Record<string, string>;

export type BuildResult = { ok: true; body: InvoiceDocumentBody } | { ok: false; errors: DraftErrors };

// eslint-disable-next-line no-control-regex -- refusing control characters is the point
const CONTROL = /[\u0000-\u001f\u007f]/;

function calendarDateError(value: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return 'dateFormat';
  const [year, month, day] = value.split('-').map(Number) as [number, number, number];
  if (year < 1970) return 'dateInvalid';
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day ? null : 'dateInvalid';
}

const codeOf = (error: Extract<DecimalResult<unknown>, { ok: false }>['error']): string => (error === 'format' ? 'number' : error === 'decimals' ? 'decimals' : 'range');

function links(
  items: readonly LinkDraft[],
  path: string,
  errors: DraftErrors,
): { id: string; quantity: number }[] {
  if (items.length > MAX_LINKS_PER_LINE) errors[path] = 'tooManyLinks';
  const result: { id: string; quantity: number }[] = [];
  items.forEach((item, index) => {
    const at = `${path}.${String(index)}`;
    if (item.refId === '') errors[`${at}.ref`] = 'required';
    const quantity = parseSignedInteger(item.quantity, MAX_QUANTITY);
    if (!quantity.ok) errors[`${at}.quantity`] = quantity.error === 'range' ? 'range' : 'wholeNumber';
    else if (quantity.value < 1) errors[`${at}.quantity`] = 'positive';
    else result.push({ id: item.refId, quantity: quantity.value });
  });
  return result;
}

export function buildDocument(draft: InvoiceDraft): BuildResult {
  const errors: DraftErrors = {};

  const number = draft.invoiceNumber.trim();
  if (number === '') errors['invoiceNumber'] = 'required';
  else if (number.length > 100) errors['invoiceNumber'] = 'tooLong';
  else if (CONTROL.test(number)) errors['invoiceNumber'] = 'controlCharacters';

  if (draft.invoiceDate === '') errors['invoiceDate'] = 'required';
  else {
    const dateError = calendarDateError(draft.invoiceDate);
    if (dateError) errors['invoiceDate'] = dateError;
  }

  const money = (field: string, text: string, required: boolean): number | null => {
    if (text.trim() === '') {
      if (required) errors[field] = 'required';
      return required ? null : 0;
    }
    const parsed = eurosToCents(text);
    if (!parsed.ok) {
      errors[field] = codeOf(parsed.error);
      return null;
    }
    return parsed.value;
  };

  const shippingNet = money('shippingNet', draft.shippingNet, false);
  let shippingVat: number | null = null;
  if (draft.shippingVat.trim() !== '') {
    const parsed = parsePercent(draft.shippingVat);
    if (!parsed.ok) errors['shippingVat'] = codeOf(parsed.error);
    else shippingVat = parsed.value;
  } else if (shippingNet !== null && shippingNet !== 0) {
    errors['shippingVat'] = 'shippingVatRequired';
  }
  const statedNet = money('statedNet', draft.statedNet, true);
  const statedVat = money('statedVat', draft.statedVat, true);
  const statedGross = money('statedGross', draft.statedGross, true);
  if (statedNet !== null && statedVat !== null && statedGross !== null && statedGross !== statedNet + statedVat) {
    errors['statedGross'] = 'grossMismatch';
  }

  if (draft.lines.length === 0) errors['lines'] = 'noLines';
  if (draft.lines.length > MAX_LINES) errors['lines'] = 'tooManyLines';

  const credit = draft.documentKind === 'credit_note';
  if (credit) {
    if ((statedNet !== null && statedNet > 0) || (statedVat !== null && statedVat > 0)) errors['statedNet'] = errors['statedNet'] ?? 'creditTotals';
    if (shippingNet !== null && shippingNet > 0) errors['shippingNet'] = 'creditTotals';
  } else if (shippingNet !== null && shippingNet < 0) {
    errors['shippingNet'] = 'invoiceShippingNegative';
  }

  const lines: InvoiceLineBody[] = [];
  draft.lines.forEach((line, index) => {
    const at = `lines.${String(index)}`;
    const description = line.description.trim();
    if (description === '') errors[`${at}.description`] = 'required';
    else if (description.length > 200) errors[`${at}.description`] = 'tooLong';
    else if (CONTROL.test(description)) errors[`${at}.description`] = 'controlCharacters';

    const quantity = parseSignedInteger(line.quantity, MAX_QUANTITY);
    let quantityValue = 0;
    if (line.quantity.trim() === '') errors[`${at}.quantity`] = 'required';
    else if (!quantity.ok) errors[`${at}.quantity`] = quantity.error === 'range' ? 'range' : 'wholeNumber';
    else if (quantity.value === 0) errors[`${at}.quantity`] = 'zero';
    else if (credit && quantity.value > 0) errors[`${at}.quantity`] = 'creditNegative';
    else quantityValue = quantity.value;

    let unitPrice = 0;
    if (line.unitPrice.trim() === '') errors[`${at}.unitPrice`] = 'required';
    else {
      const parsed = eurosToUnitCents(line.unitPrice);
      if (!parsed.ok) errors[`${at}.unitPrice`] = parsed.error === 'format' ? 'number' : parsed.error === 'decimals' ? 'unitDecimals' : 'unitRange';
      else unitPrice = parsed.value;
    }

    let discount = 0;
    if (line.discount.trim() !== '') {
      const parsed = parsePercent(line.discount);
      if (!parsed.ok) errors[`${at}.discount`] = parsed.error === 'format' ? 'number' : parsed.error === 'decimals' ? 'percentDecimals' : 'percentRange';
      else discount = parsed.value;
    }

    let vat = 0;
    if (line.vat.trim() === '') errors[`${at}.vat`] = 'required';
    else {
      const parsed = parsePercent(line.vat);
      if (!parsed.ok) errors[`${at}.vat`] = parsed.error === 'format' ? 'number' : parsed.error === 'decimals' ? 'percentDecimals' : 'percentRange';
      else vat = parsed.value;
    }

    let statedLineNet: number | null = null;
    if (line.statedNet.trim() !== '') {
      const parsed = eurosToCents(line.statedNet);
      if (!parsed.ok) errors[`${at}.statedNet`] = codeOf(parsed.error);
      else statedLineNet = parsed.value;
    }

    // Only the links the form SHOWS for this sign are sent (receipt allocations on a goods line, return links on a credit line). The
    // draft itself drops the other kind when the sign changes (`reconcileLinks`); this is the second guard so that a hidden row can
    // never reach the wire or raise an error the user cannot see.
    const receiptAllocations = (quantityValue > 0 ? links(line.allocations, `${at}.alloc`, errors) : []).map((link) => ({ receiptLineId: link.id, quantity: link.quantity }));
    const creditLinks = (quantityValue < 0 ? links(line.creditLinks, `${at}.credit`, errors) : []).map((link) => ({ returnLineId: link.id, quantity: link.quantity }));
    receiptAllocations.sort((a, b) => a.receiptLineId.localeCompare(b.receiptLineId));
    creditLinks.sort((a, b) => a.returnLineId.localeCompare(b.returnLineId));

    lines.push({
      lineNumber: index + 1,
      purchaseOrderLineId: line.poLineId === '' ? null : line.poLineId,
      description,
      quantity: quantityValue,
      unitPriceNetCents: unitPrice,
      discountPercent: discount,
      vatPercent: vat,
      statedLineNetCents: statedLineNet,
      receiptAllocations,
      creditLinks,
    });
  });

  if (Object.keys(errors).length > 0 || shippingNet === null || statedNet === null || statedVat === null || statedGross === null) {
    return { ok: false, errors };
  }
  return {
    ok: true,
    body: {
      documentKind: draft.documentKind,
      invoiceNumber: number,
      invoiceDate: draft.invoiceDate,
      currency: 'EUR',
      shipping: { netCents: shippingNet, vatPercent: shippingVat },
      stated: { netTotalCents: statedNet, vatTotalCents: statedVat, grossTotalCents: statedGross },
      lines,
    },
  };
}

/**
 * A line whose quantity now has a sign that excludes one kind of link loses exactly those links: a goods line (quantity > 0) has no
 * return links, a credit line (quantity < 0) has no receipt allocations (the matcher refuses both as `credit_link_on_non_credit_line`
 * / `allocation_on_non_goods_line`). Text that is not a whole number, and 0, change nothing: the quantity error is shown and no link
 * is sent meanwhile. The links are REMOVED, not parked, so changing the sign back never revives them; `removed` names what went so the
 * form can say it.
 */
export type RemovedLinks = 'allocations' | 'creditLinks';

export function reconcileLinks(line: LineDraft): { line: LineDraft; removed: RemovedLinks | null } {
  const quantity = parseSignedInteger(line.quantity, MAX_QUANTITY);
  if (!quantity.ok) return { line, removed: null };
  if (quantity.value > 0 && line.creditLinks.length > 0) return { line: { ...line, creditLinks: [] }, removed: 'creditLinks' };
  if (quantity.value < 0 && line.allocations.length > 0) return { line: { ...line, allocations: [] }, removed: 'allocations' };
  return { line, removed: null };
}

/** Whether the note "these links were removed" still describes the line: not once the sign that removed them has flipped back. */
export function removedStillApplies(removed: RemovedLinks, quantityText: string): boolean {
  const quantity = parseSignedInteger(quantityText, MAX_QUANTITY);
  if (!quantity.ok) return true;
  return removed === 'allocations' ? quantity.value < 0 : quantity.value > 0;
}

export const MAX_REASON_LENGTH = 500;

/**
 * The server's `text(500)` for the reason of a decision: trimmed, not empty, at most 500 characters and no control character (a line
 * break or a tab included). The same check the invoice number and the line description get. `null` = acceptable. The text is never
 * rewritten here: the user is told what to change.
 */
export function decisionReasonError(reason: string): 'required' | 'tooLong' | 'controlCharacters' | null {
  const trimmed = reason.trim();
  if (trimmed.length === 0) return 'required';
  if (trimmed.length > MAX_REASON_LENGTH) return 'tooLong';
  if (CONTROL.test(trimmed)) return 'controlCharacters';
  return null;
}

/**
 * The identity of a document for fencing: two documents are the same exactly when this text is. Built from the wire document, whose
 * keys are produced in one fixed order and whose links are sorted, so it is the same text the server canonicalises.
 */
export const canonicalOf = (body: InvoiceDocumentBody): string => JSON.stringify(body);

/** The canonical text of the draft as it stands now, or `null` while it cannot be sent (incomplete or invalid). */
export function canonicalOfDraft(draft: InvoiceDraft): string | null {
  const built = buildDocument(draft);
  return built.ok ? canonicalOf(built.body) : null;
}

/**
 * Copies the server's allocation proposals into the draft as ordinary allocations the user can still edit. Quantities already
 * allocated to the same receipt line are increased, never replaced. This CHANGES the document: the old review no longer applies.
 */
export function applyProposals(draft: InvoiceDraft, proposals: readonly AllocationProposal[]): InvoiceDraft {
  return {
    ...draft,
    lines: draft.lines.map((line, index) => {
      const proposal = proposals.find((candidate) => candidate.lineNumber === index + 1 && candidate.purchaseOrderLineId.toLowerCase() === line.poLineId.toLowerCase());
      if (!proposal || proposal.allocations.length === 0) return line;
      const allocations = line.allocations.map((allocation) => ({ ...allocation }));
      for (const proposed of proposal.allocations) {
        const existing = allocations.find((allocation) => allocation.refId === proposed.receiptLineId);
        if (existing) {
          const current = parseSignedInteger(existing.quantity, MAX_QUANTITY);
          existing.quantity = String((current.ok ? current.value : 0) + proposed.quantity);
        } else {
          allocations.push({ id: newLocalId(), refId: proposed.receiptLineId, quantity: String(proposed.quantity) });
        }
      }
      return { ...line, allocations };
    }),
  };
}
